import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isCustomTool, getCustomToolId } from '@/config/tools';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { ToolPreview } from '@/components/ToolPreview';

// Tool configurations with icons and colors
import {
  Grid3X3,
  Users,
  Shield,
  BookOpen,
  Calendar1,
  Calendar,
  CheckSquare,
  MessageCircle,
  FileText,
  MessageSquare,
  CheckCircle2
} from 'lucide-react';

export const toolConfigs = {
  matrix: {
    name: 'Matrix',
    icon: Grid3X3,
    description: 'Training competency matrices',
    color: 'bg-red-50 border-red-200',
    iconColor: 'text-red-600',
    content: 'Manage skills and certifications across your workforce'
  },
  team: {
    name: 'Team',
    icon: Users,
    description: 'Team member management',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
    content: 'Track roles, responsibilities and progress'
  },
  team_compliance: {
    name: 'Team Compliance',
    icon: Shield,
    description: 'Team training & certifications',
    color: 'bg-green-50 border-green-200',
    iconColor: 'text-green-600',
    content: 'Manage training requirements and compliance for team members'
  },
  site_compliance: {
    name: 'Site Compliance',
    icon: Shield,
    description: 'Site safety & inspections',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    content: 'Track site-specific compliance, inspections, and safety documentation'
  },
  qualifications: {
    name: 'Qualifications',
    icon: BookOpen,
    description: 'Training requirements',
    color: 'bg-orange-50 border-orange-200',
    iconColor: 'text-orange-600',
    content: 'Manage qualifications and certifications needed'
  },
  bookings: {
    name: 'Bookings',
    icon: Calendar1,
    description: 'Training session scheduling',
    color: 'bg-purple-50 border-purple-200',
    iconColor: 'text-purple-600',
    content: 'Schedule workshops and resource bookings'
  },
  calendar: {
    name: 'Calendar',
    icon: Calendar,
    description: 'Important dates and milestones',
    color: 'bg-pink-50 border-pink-200',
    iconColor: 'text-pink-600',
    content: 'Set deadlines and sync with external calendars'
  },
  checklists: {
    name: 'Tasks',
    icon: CheckSquare,
    description: 'Task management and tracking',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    content: 'Manage and track tasks across your team'
  },
  // Alias - new spaces store 'tasks' key instead of 'checklists'
  tasks: {
    name: 'Tasks',
    icon: CheckSquare,
    description: 'Task management and tracking',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    content: 'Manage and track tasks across your team'
  },
  chat: {
    name: 'Chat',
    icon: MessageCircle,
    description: 'Real-time team communication',
    color: 'bg-indigo-50 border-indigo-200',
    iconColor: 'text-indigo-600',
    content: 'Direct messages, group chats, and file sharing'
  },
  docs: {
    name: 'Docs & Files',
    icon: FileText,
    description: 'Document management',
    color: 'bg-amber-50 border-amber-200',
    iconColor: 'text-amber-600',
    content: 'Store and collaborate on documents and training materials'
  },
  message_board: {
    name: 'Posts',
    icon: MessageSquare,
    description: 'Team announcements',
    color: 'bg-cyan-50 border-cyan-200',
    iconColor: 'text-cyan-600',
    content: 'Post updates and discussions with threaded conversations'
  },
  checkins: {
    name: 'Check-ins',
    icon: CheckCircle2,
    description: 'Team status updates',
    color: 'bg-teal-50 border-teal-200',
    iconColor: 'text-teal-600',
    content: 'Regular check-ins to track progress and team sentiment'
  },
  attendance: {
    name: 'Attendance',
    icon: Calendar,
    description: 'Leave and shift management',
    color: 'bg-violet-50 border-violet-200',
    iconColor: 'text-violet-600',
    content: 'Track holidays, absences, and shift patterns for your team'
  }
  // PPE tool temporarily hidden - not released yet
  // ppe: {
  //   name: 'PPE Management',
  //   icon: HardHat,
  //   description: 'PPE equipment tracking',
  //   color: 'bg-yellow-50 border-yellow-200',
  //   iconColor: 'text-yellow-600',
  //   content: 'Manage PPE equipment issuance, returns, and inventory tracking'
  // }
};

// ToolCard component - extracted to module level for proper memoization
export const ToolCard = memo(({
  toolKey,
  config,
  projectId,
  dragListeners,
  dragAttributes,
  onRemoveTool,
  previewData
}) => {
  const navigate = useNavigate();

  // Define which tools are coming soon (all tools are now working!)
  const comingSoonTools = [];
  const isComingSoon = comingSoonTools.includes(toolKey);

  // All hooks must be called before any conditional returns
  const handleClick = useCallback(() => {
    if (!config) return;
    if (isComingSoon) return;

    // Handle custom tools
    if (isCustomTool(toolKey)) {
      const customToolId = getCustomToolId(toolKey);
      const slug = config.customToolData?.slug || customToolId;
      navigate(`/space/${projectId}/tool/${slug}`);
      return;
    }

    const routes = {
      matrix: `/space/${projectId}/matrix`,
      team: `/space/${projectId}/team`,
      team_compliance: `/space/${projectId}/compliance`,
      site_compliance: `/space/${projectId}/site-compliance`,
      qualifications: `/space/${projectId}/qualifications`,
      bookings: `/space/${projectId}/bookings`,
      calendar: `/space/${projectId}/schedule`,
      checklists: `/space/${projectId}/tasks`,
      tasks: `/space/${projectId}/tasks`,
      chat: `/chat?space=${projectId}`,
      docs: `/space/${projectId}/docs`,
      message_board: `/space/${projectId}/message-board`,
      checkins: `/space/${projectId}/checkins`,
      attendance: `/space/${projectId}/attendance`,
      ppe: `/space/${projectId}/ppe`
    };

    if (routes[toolKey]) {
      navigate(routes[toolKey]);
    }
  }, [toolKey, projectId, navigate, isComingSoon, config]);

  const handleRemoveClick = useCallback((e) => {
    e.stopPropagation();
    if (config) {
      onRemoveTool?.({ key: toolKey, name: config.name });
    }
  }, [toolKey, config, onRemoveTool]);

  // Handle cases where config might be undefined for unknown tools
  if (!config) {
    console.warn(`No configuration found for tool: ${toolKey}`);
    return null;
  }

  const IconComponent = config.icon;

  return (
    <Card
      className={`group ${
        isComingSoon ? 'opacity-70' : 'hover:shadow-xl cursor-pointer'
      } transition-all duration-300 h-full flex flex-col border-0 overflow-hidden relative ${
        !isComingSoon ? 'hover:scale-[1.02]' : ''
      }`}
      onClick={handleClick}
      {...dragListeners}
      {...dragAttributes}
    >
      {/* Coming Soon Banner */}
      {isComingSoon && (
        <div className='absolute top-0 right-0 bg-yellow-500 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg rounded-tr-md z-10'>
          Coming Soon
        </div>
      )}

      <CardHeader className='pb-2'>
        <div className='flex items-start justify-between gap-3'>
          <div className='flex items-start gap-2 flex-1 overflow-hidden'>
            {/* Support both Tailwind classes (built-in tools) and inline styles (custom tools) */}
            {config.isCustom ? (
              <div
                className="p-1.5 rounded-lg flex-shrink-0"
                style={{ backgroundColor: config.customToolData?.color ? `${config.customToolData.color}20` : '#6366f120' }}
              >
                <IconComponent
                  className={`h-4 w-4 ${isComingSoon ? 'opacity-60' : ''}`}
                  style={{ color: config.customToolData?.color || '#6366f1' }}
                />
              </div>
            ) : (
              <div className={`p-1.5 ${config.color} rounded-lg flex-shrink-0`}>
                <IconComponent
                  className={`h-4 w-4 ${config.iconColor} ${
                    isComingSoon ? 'opacity-60' : ''
                  }`}
                />
              </div>
            )}
            <div className='flex-1 overflow-hidden'>
              <CardTitle
                className={`text-base font-semibold leading-tight ${
                  isComingSoon ? 'opacity-80' : ''
                }`}
              >
                {config.name}
              </CardTitle>
              <CardDescription
                className={`text-xs mt-1 whitespace-nowrap overflow-hidden text-ellipsis ${
                  isComingSoon ? 'opacity-70' : ''
                }`}
              >
                {config.description}
              </CardDescription>
            </div>
          </div>
          {!isComingSoon && (
            <div className='flex items-center gap-1 flex-shrink-0'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity'
                    onClick={e => e.stopPropagation()}
                    onPointerDown={e => e.stopPropagation()}
                  >
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='end'
                  onClick={e => e.stopPropagation()}
                >
                  <DropdownMenuItem onClick={handleRemoveClick}>
                    <Trash2 className='h-4 w-4 mr-2' />
                    Remove Tool
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className='flex-1 flex flex-col pt-0'>
        {/* Preview Section */}
        {!isComingSoon && (
          <div className='flex-1 border-t pt-3'>
            {config.isCustom ? (
              /* Custom tool preview - show URL */
              <div className='space-y-2'>
                <p className='text-xs text-muted-foreground line-clamp-2'>
                  {config.customToolData?.url || 'External tool'}
                </p>
                <div className='flex items-center gap-1.5'>
                  <div
                    className='w-2 h-2 rounded-full'
                    style={{ backgroundColor: config.customToolData?.color || '#6366f1' }}
                  />
                  <span className='text-xs text-muted-foreground'>Embedded content</span>
                </div>
              </div>
            ) : (
              <ToolPreview
                toolKey={toolKey}
                projectId={projectId}
                previewData={previewData}
              />
            )}
          </div>
        )}

        {isComingSoon && (
          <div className='flex-1 flex items-center justify-center'>
            <div className='text-center space-y-2'>
              <div className='text-3xl opacity-20'>🚧</div>
              <p className='text-sm text-muted-foreground'>
                This feature will be available soon
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

ToolCard.displayName = 'ToolCard';

// SortableToolCard wrapper - extracted to module level
export const SortableToolCard = memo(({
  toolKey,
  config,
  projectId,
  onRemoveTool,
  previewData
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: toolKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  return (
    <div ref={setNodeRef} style={style} className='w-full h-full'>
      <ToolCard
        toolKey={toolKey}
        config={config}
        projectId={projectId}
        dragListeners={listeners}
        dragAttributes={attributes}
        onRemoveTool={onRemoveTool}
        previewData={previewData}
      />
    </div>
  );
});

SortableToolCard.displayName = 'SortableToolCard';
